/**
 * Libraries
 */

import { defineStore } from 'pinia';

/**
 * Services
 */
import * as contractService from '@modules/contract/api/contract';

/**
 * Types
 */

import type {
  PiniaCustomProperties,
  _GettersTree,
  _StoreWithGetters,
} from 'pinia';

import type { RouteParamValue } from 'vue-router';

import type { UnwrapRef } from 'vue';

import type {
  Ticket,
  PaginatorInfo,
  EditContractAttendeeInput,
  DeleteContractAttendeeInput,
  Maybe,
} from '@generatedTypes';

import type { TicketLifeCyclePatchType } from '@modules/tickets/types/Ticket';

export const TICKET_STORE_ID = 'tickets';

export interface Tickets {
  tickets: Ticket[];
  paginatorInfo?: PaginatorInfo[];
  currentTicket?: Maybe<Ticket>;
  ticketCode: Ticket['code'];
}

export interface DeleteInput {
  id: string;
}

export interface TrashInput {
  id: string;
}

export interface Pagination {
  count: number;
  currentPage: number;
  firstItem: number;
  hasMorePages: boolean;
  lastItem: number;
  lastPage: number;
  perPage: number;
  total: number;
}

/**
 * Type to allow us to declare store getters in spearated files and keep typings
 *
 * @see ./promo-front/node_modules/pinia/dist/pinia.d.ts line 205
 */

type _TicketGettersTree = _GettersTree<Tickets>;
export type TicketGetters = ThisType<
  UnwrapRef<Tickets> &
    _StoreWithGetters<_TicketGettersTree> &
    PiniaCustomProperties
> &
  _TicketGettersTree;

/**
 * Some though about refactoring the store
 *
 * Solution 1 :
 * Refactor this store with the https://pinia.vuejs.org/core-concepts/#setup-stores
 * in order to made the middleware more useful https://github.com/vuejs/pinia/discussions/988
 * Full workflow :
 * - in the middleware, we query the ticket
 * - after the query resolve we set the ticket in the setup store
 * - the value is reactive so when we call the variable in the component the value update itself ❤️
 *
 * Solution 2 :
 * Remove the use of the store for storing data from the server
 * Instead use the store to store synchronous and front-end logic only, like state of the tabulation after quitting a page
 *
 * Solution 3 :
 * Remove the use of the store for storing data from the server
 * Use the component in the page folder to handle asynchronous call and pass the data directly with inject https://vuejs.org/guide/components/provide-inject.html
 * Full workflow :
 * - We get the ticket at the hightest component available in the pages folder ( contract.vue for example )
 * - We provide the currentTicket, refetch and other methods necessary
 * - In the children we use inject to get the currentTicket information
 * // TODO:Legacy / deprecated. use Apollo Client instead.
 */
export const useTicketStore = defineStore(TICKET_STORE_ID, {
  state: () =>
    ({
      tickets: [],
      pagination: [],
      currentTicket: null,
      ticketCode: '',
    }) as Tickets,

  getters: {
    getCurrentTicket: (state) => () => state.currentTicket,
    ticketByCode: (state) => (code: RouteParamValue) =>
      state.tickets.find((ticket) => ticket.code === code),
  },

  actions: {
    setCurrentTicket(ticket: Ticket) {
      this.currentTicket = ticket;
    },
    syncCurrentTicket(ticketPatch: TicketLifeCyclePatchType) {
      if (this.currentTicket?.id !== ticketPatch.id) {
        return;
      }

      this.currentTicket = {
        ...this.currentTicket,
        ...ticketPatch,
      };
    },
    setCurrentTicketCode(code: Ticket['code']) {
      this.ticketCode = code;
    },
    editContractAttendee(input?: EditContractAttendeeInput) {
      return contractService.editContractAttendee(input);
    },
    deleteContractAttendee(input?: DeleteContractAttendeeInput) {
      return contractService.deleteContractAttendee(input);
    },
  },
  persist: true,
});
